/**
 * Small versions of public images, made by Cloudflare Image Transformations
 * on the asset domain (`<origin>/cdn-cgi/image/<options>/<path>`).
 *
 * Covers are stored as uploaded, and most exercise covers are 1–3 MB PNGs. A
 * card shows them a few hundred pixels wide, so a phone grid of them downloads
 * tens of megabytes before the covers appear.
 *
 * Resizing only works on a Cloudflare zone that has Transformations switched
 * on, which is a dashboard setting, not code. So it is off until the build
 * names the asset origins that support it (`IMAGE_RESIZE_ORIGINS`). Any other
 * URL, including a signed one, is used as it is.
 */

/**
 * The longest side card and row images ask for. Exercise cards are a 4:3 box
 * with `object-fit: cover`, so a 16:9 cover is cropped at the sides and its
 * HEIGHT sets the sharpness: a one-column phone card (about 380 x 285 CSS px)
 * at 3x needs about 855 rows, and 1280 x 720 is within 1.2x of that. One size
 * for every card and row keeps one cached copy and one Cloudflare
 * transformation per image.
 */
export const CARD_IMAGE_MAX_SIDE = 1280;

let resizeOrigins: readonly string[] = [];

/**
 * How many small versions may fail on an origin, with none loaded, before this
 * page stops asking that origin for them. Every failure costs a request before
 * the original, so a zone with Transformations off (or out of quota) would
 * otherwise cost one wasted request per image for the whole visit.
 */
export const RESIZE_FAILURES_BEFORE_OFF = 3;

const outcomes = new Map<
  string,
  { failed: Set<string>; loaded: Set<string> }
>();

const outcomesFor = (origin: string) => {
  let entry = outcomes.get(origin);
  if (!entry) {
    entry = { failed: new Set(), loaded: new Set() };
    outcomes.set(origin, entry);
  }
  return entry;
};

// Bumped whenever the answer of `getResizedImageUrl` can change, so images
// already rendered with a small URL they have not fetched yet (lazy, below
// the fold) switch to the original instead of failing one by one.
let resizeVersion = 0;
const listeners = new Set<() => void>();

const announceChange = () => {
  resizeVersion++;
  listeners.forEach((listener) => listener());
};

/** For `useSyncExternalStore`: re-render when resizing switches off. */
export const subscribeToImageResize = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getImageResizeVersion = () => resizeVersion;

const isSwitchedOff = (origin: string) => {
  const entry = outcomes.get(origin);
  return (
    !!entry &&
    entry.loaded.size === 0 &&
    entry.failed.size >= RESIZE_FAILURES_BEFORE_OFF
  );
};

/**
 * What became of an image `CdnImage` showed. Only small versions count; one
 * that loads keeps resizing on for good, since failures after that are about
 * single files. Each URL counts once, however many events it fires.
 */
export const reportImageOutcome = (
  imageUrl: string,
  outcome: 'failed' | 'loaded',
) => {
  let url: URL;
  try {
    url = new URL(imageUrl);
  } catch {
    return;
  }
  if (!url.pathname.startsWith('/cdn-cgi/image/')) return;
  if (!resizeOrigins.includes(url.origin)) return;

  const wasOff = isSwitchedOff(url.origin);
  outcomesFor(url.origin)[outcome].add(imageUrl);
  if (!wasOff && isSwitchedOff(url.origin)) {
    // eslint-disable-next-line no-console
    console.warn(
      `IMAGE_RESIZE_ORIGINS: ${url.origin} returned no small image, so this page uses originals. Are Cloudflare Transformations on for this zone?`,
    );
    announceChange();
  }
};

/**
 * Accepts a comma-separated list of origins, e.g. `https://assets.example.com`.
 * Each entry is normalised the way `URL.origin` is (case, default port, path),
 * so a slightly different spelling still matches. An entry that is not a URL
 * is dropped with a warning instead of silently matching nothing.
 */
export const setImageResizeOrigins = (origins: string | undefined) => {
  outcomes.clear();
  resizeOrigins = (origins ?? '')
    .split(',')
    .map((entry) => entry.trim().replace(/^["']|["']$/g, ''))
    .filter(Boolean)
    .flatMap((entry) => {
      try {
        const { origin, protocol } = new URL(entry);
        if (protocol === 'http:' || protocol === 'https:') return [origin];
      } catch {
        // falls through to the warning
      }
      // A misspelt build setting is otherwise invisible: resizing just stays off
      // eslint-disable-next-line no-console
      console.warn(`IMAGE_RESIZE_ORIGINS: "${entry}" is not an http(s) origin`);
      return [];
    });
};

/**
 * The resized URL for `src`, or `undefined` when `src` is not on a resizing
 * origin. The image fits inside `maxSide` x `maxSide` with its aspect kept;
 * `fit=scale-down` never enlarges a small one.
 *
 * No `onerror=redirect`, on purpose: it would send the original in place of
 * an error, so `CdnImage` would count it as a small version that loaded. A
 * zone out of its free quota (error 9422 on every new transformation) would
 * then never switch resizing off. Without it the error reaches `CdnImage`,
 * which loads the original itself, for the same one extra request.
 */
export const getResizedImageUrl = (
  src: string | undefined,
  maxSide: number | undefined,
) => {
  if (!src || !maxSide || resizeOrigins.length === 0) return undefined;

  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return undefined;
  }

  // `blob:` and `data:` URLs can report the page's own origin.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
  if (!resizeOrigins.includes(url.origin)) return undefined;
  if (isSwitchedOff(url.origin)) return undefined;
  // A query string means a signed or otherwise special URL, not a plain asset.
  if (url.search || url.pathname.startsWith('/cdn-cgi/')) return undefined;

  const side = Math.round(maxSide);
  const options = [
    `width=${side}`,
    `height=${side}`,
    'fit=scale-down',
    'format=auto',
  ].join(',');

  return `${url.origin}/cdn-cgi/image/${options}${url.pathname}`;
};
